import React, { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { updateTask } from 'actions/tasks';

import { STORY_POINT_DURATION, getTaskStart, getTaskEnd, getTaskDuration } from 'utils/task';
import { useCanUser } from 'shared-hooks/permission';
import { useTaskGroup } from 'shared-hooks/task-group';

import ProjectName from 'components/project-name';
import TaskGroupName from 'components/task-group-name';
import UserFaces from 'components/user-faces';
import TimelineDraggable from 'components/TimelineDraggable';
import Tooltip from 'components/tooltip';

import IconArrowRight from 'icons/icon-arrow-right';

import './TimelineTask.styles.css';

const handleStyles = {
    left: {
        top: '5px',
        bottom: '5px'
    },
    right: {
        top: '5px',
        bottom: '5px'
    }
};

const Task = ({ completed, markerStyle, taskName, showProject, projectId, taskGroupId, otherAssignees, start, end, onClick, onOverflowChange }) => {
    const { t } = useTranslation('task');
    const element = useRef(null);
    const innerElement = useRef(null);
    const textElement = useRef(null);
    const secondTextElement = useRef(null);
    const [overflowing, setOverflowing] = useState(false);

    useLayoutEffect(() => {
        const innerIsOverflowing = element.current?.clientWidth < innerElement.current?.scrollWidth;
        const textIsOverflowing = textElement.current?.clientWidth < textElement.current?.scrollWidth;
        const secondTextIsOverflowing = secondTextElement.current?.clientWidth < secondTextElement.current?.scrollWidth;
        setOverflowing(innerIsOverflowing || textIsOverflowing || secondTextIsOverflowing);
    }, [element, innerElement, textElement, secondTextElement, taskName, start, end]);

    useEffect(() => {
        onOverflowChange && onOverflowChange(overflowing);
    }, [overflowing, onOverflowChange]);

    return (
        <Tooltip tooltip={t('noDates')} disabled={start}>
            <div className={'timeline-task' + (completed ? ' completed' : '')} ref={element}>
                <div className="timeline-task-inner" onClick={onClick} ref={innerElement}>
                    <div className="timeline-task-marker" style={completed ? null : markerStyle} />

                    <div className="timeline-task-head">
                        <div className="timeline-task-text-line" ref={textElement}>{ taskName }</div>
                    </div>

                    <div className="timeline-task-body">
                        <div className="timeline-task-text-line" ref={secondTextElement}>
                            {
                                showProject ? (
                                    <React.Fragment>
                                        <ProjectName id={projectId} />

                                        <IconArrowRight />
                                    </React.Fragment>
                                ) : null
                            }

                            <TaskGroupName id={taskGroupId} />
                        </div>

                        {
                            otherAssignees?.length ? (
                                <div className="timeline-task-foot">
                                    <UserFaces size="xxxsmall" users={otherAssignees} />
                                </div>
                            ) : null
                        }
                    </div>
                </div>
            </div>
        </Tooltip>
    );
}

const TimelineTask = ({ taskId, taskName, completed, projectId, taskGroupId, dueDate, startDate, completedOn, storyPoints, row, rowHeight, dayWidth, chartStart, excludedUser, showProject, assignees, draggable, boundToParent, onClick }) => {
    const [dragging, setDragging] = useState(false);
    const [resizing, setResizing] = useState(false);
    const [dragStartedPos, setDragStartedPos] = useState(null);
    const [dragStartedOn, setDragStartedOn] = useState(null);
    const [overflowing, setOverflowing] = useState(false);
    const canEditTask = useCanUser('editTask');
    const taskGroup = useTaskGroup(taskGroupId);

    const start = useMemo(() => {
        return getTaskStart({ dueDate, startDate, completedOn, storyPoints })
    }, [dueDate, startDate, completedOn, storyPoints]);

    const end = useMemo(() => {
        return getTaskEnd({ dueDate, startDate, completedOn, storyPoints })
    }, [dueDate, startDate, completedOn, storyPoints]);

    const positionProps = useMemo(() => {
        const duration = getTaskDuration({ dueDate, startDate, completedOn, storyPoints });
        const left = moment(start).diff(chartStart, 'hours') / 24 * dayWidth;

        const width = duration * dayWidth;

        return {
            left,
            width
        }
    }, [start, dueDate, startDate, completedOn, storyPoints, chartStart, dayWidth]);

    const style = useMemo(() => ({
        position: 'absolute',
        height: rowHeight,
        top: row * rowHeight,
        left: positionProps.left,
        width: positionProps.width
    }), [positionProps, row, rowHeight]);

    const markerStyle = useMemo(() => {
        if (completed || !taskGroup?.color) {
            return null;
        }

        return {
            backgroundColor: taskGroup.color
        }
    }, [completed, taskGroup?.color]);

    const otherAssignees = useMemo(() => {
        if (!assignees || !excludedUser) {
            return assignees;
        }

        return assignees.filter(assignee => assignee !== excludedUser);
    }, [excludedUser, assignees]);

    const handleClick = useCallback(event => {
        if (((resizing || dragging) && (Math.abs(dragStartedPos - event.clientX) > 10 || new Date() - dragStartedOn > 500))) {
            return;
        }

        onClick && onClick(taskId);
    }, [taskId, resizing, dragging, dragStartedPos, dragStartedOn]);

    const handleChange = useCallback((nextStart, nextEnd) => {
        updateTask(taskId, {
            start_date: nextStart,
            due_date: nextEnd
        });
    }, [taskId]);

    const handleDragStateChange = useCallback((dragging, dragStartedPos, dragStartedOn) => {
        if (dragging) {
            setDragStartedPos(dragStartedPos);
            setDragStartedOn(dragStartedOn);
        }

        setDragging(dragging);
    }, []);

    const handleOverflowChange = useCallback(overflowing => {
        setOverflowing(overflowing);
    }, []);

    const taskProps = {
        completed,
        markerStyle,
        taskName,
        showProject,
        projectId,
        taskGroupId,
        otherAssignees,
        onClick: handleClick,
        start,
        end,
        onOverflowChange: handleOverflowChange
    }

    if (!draggable || !canEditTask) {
        return (
            <div className="timeline-task-holder">
                <div style={style}>
                    <Task {...taskProps} />
                </div>
            </div>
        );
    }

    return (
        <div className="timeline-task-holder" style={{ top: row * rowHeight }}>
            <TimelineDraggable
                start={start}
                end={end}
                chartStartDate={chartStart}
                dayWidth={dayWidth}
                minHeight={rowHeight}
                tooltipPlacement={(!isNaN(row) && row < 2 ? 'bottom' : 'top')}
                overflowing={overflowing}
                boundToParent={boundToParent}
                handleStyles={handleStyles}
                onChange={handleChange}
                onResizingChange={setResizing}
                onDraggingChange={handleDragStateChange}
            >
                <Task {...taskProps} />
            </TimelineDraggable>
        </div>
    )
}

export default React.memo(TimelineTask);
